import React, { Component } from 'react';
import { Grid, Form } from 'semantic-ui-react';
import {
  reduxForm,
  FormSection,
  touch,
  startAsyncValidation,
  stopAsyncValidation,
} from 'redux-form';
import * as _ from 'lodash';

import { APPLICATION_STUDENT_FORM_NAME } from 'Common/FormEnum';
import { ApplicationFieldEnum as Fields } from 'Common/FieldEnum';
import { validateIncome } from '../../helpers/validation/IncomeValidator';
import { EthnicityEnum } from 'Common/Constants/Enumeration/EthnicityEnum';
import { convertEnumToDropdownArray } from 'Common/Constants/Enumeration/ConvertEnumToDropdownArray';
import { RaceEnum } from 'Common/Constants/Enumeration/RaceEnum';
import { ExceptionsForm } from './ExceptionsForm';
import { StudentInformationForm } from './StudentInformationForm';
import { IncomeForm } from '../hub/IncomeForm';
import { LanguageSsnForm } from '../hoh/HOHLanguageSSNForm';
import MPWApi from 'API/MPWApi';
import { validateExceptions } from '../../helpers/validation/ExceptionsValidator';
import { TitledCard } from 'Components';

class StudentForm extends Component {
  componentDidUpdate(prevProps) {
    const { formValues, dispatch } = this.props;

    if (
      formValues[Fields.Id] !== prevProps.formValues[Fields.Id] ||
      formValues[Fields.ServingNumber] !==
        prevProps.formValues[Fields.ServingNumber]
    ) {
      this.debouncedAsyncValidate();
    }

    if (!formValues[Fields.Id] || formValues[Fields.Id].length < 3) {
      dispatch(stopAsyncValidation(APPLICATION_STUDENT_FORM_NAME));
    }
  }

  debouncedAsyncValidate = _.debounce(() => {
    this.props.asyncValidate();
  }, 200);

  getGradesOptions = () => {
    return this.props.gradesLookup.map((item, index) => {
      return {
        key: index,
        value: item.Id,
        text: `${item.Id} ${item.Description}`,
      };
    });
  };

  ethnicities = convertEnumToDropdownArray(EthnicityEnum);

  races = () => {
    const raceList = convertEnumToDropdownArray(RaceEnum);
    const noResponseIndex = raceList.findIndex(race => {
      return race.value === 8;
    });

    if (noResponseIndex > -1) {
      const noResponse = raceList[noResponseIndex];
      raceList.splice(noResponseIndex, 1);
      raceList.unshift(noResponse);
    }

    return raceList;
  };

  getCaseTypeOptions = () => {
    const { formValues, caseTypes } = this.props;
    const hideDcOnly = !(
      formValues.Exceptions && formValues.Exceptions[Fields.IsDirectCertified]
    );

    return caseTypes
      .filter(caseType =>
        hideDcOnly ? !caseType.IsDCOnly && caseType.Enabled : caseType.Enabled
      )
      .map(caseType => ({
        key: caseType.Id,
        value: caseType.Id,
        text: caseType.Description,
      }));
  };

  getEmancipatedExtraInfo = () => {
    const { formValues } = this.props;
    if (
      _.has(formValues, `Exceptions.${Fields.IsEmancipatedMinor}`) &&
      formValues.Exceptions[Fields.IsEmancipatedMinor]
    ) {
      return (
        <LanguageSsnForm className={'manage-application--language-ssn-grid'} />
      );
    }

    return null;
  };

  getServingNumber = () => {
    MPWApi.getGeneric('student/GenerateServingNum').then(values => {
      this.props.change(Fields.ServingNumber, values.data.Data.toString());
    });
  };

  changeStudentNumber = (event, value, prev) => {
    const { formValues, change, dispatch } = this.props;

    event.preventDefault();

    change(Fields.Id, value);
    dispatch(startAsyncValidation(APPLICATION_STUDENT_FORM_NAME, Fields.Id));

    if (
      formValues[Fields.ServingNumber] === prev ||
      formValues[Fields.ServingNumber] === null
    ) {
      change(Fields.ServingNumber, value);
      dispatch(touch(APPLICATION_STUDENT_FORM_NAME, Fields.ServingNumber));
    }
  };

  render() {
    const { formValues, change, dispatch, isNewStudent, errors } = this.props;

    return (
      <Form>
        <Grid>
          <Grid.Row>
            <Grid.Column>
              <TitledCard title={'Information'}>
                <StudentInformationForm
                  isNewStudent={isNewStudent}
                  raceOptions={this.races()}
                  ethnicityOptions={this.ethnicities}
                  gradeOptions={this.getGradesOptions()}
                  changeServingNumber={this.getServingNumber}
                  changeStudentNumber={this.changeStudentNumber}
                />
                {this.getEmancipatedExtraInfo()}
              </TitledCard>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <FormSection name={'Exceptions'}>
                <TitledCard title={'Categorical Information'}>
                  <fieldset
                    className="fieldset--clean"
                    disabled={this.props.isIncomplete}
                  >
                    <ExceptionsForm
                      caseTypes={this.getCaseTypeOptions()}
                      change={change}
                      dispatch={dispatch}
                      error={errors ? errors[Fields.Exceptions] : null}
                      values={formValues.Exceptions || {}}
                      isIncomplete={this.props.isIncomplete}
                    />
                  </fieldset>
                </TitledCard>
              </FormSection>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <FormSection name="Income">
                <IncomeForm memberName={'Student'} />
              </FormSection>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Form>
    );
  }
}

const validate = values => {
  const errors = {};

  if (!values[Fields.FirstName] || values[Fields.FirstName].trim().length < 1) {
    errors[Fields.FirstName] = 'First name is required.';
  }

  if (!values[Fields.LastName] || values[Fields.LastName].trim().length < 1) {
    errors[Fields.LastName] = 'Last name is required.';
  }

  if (
    !values[Fields.ServingNumber] ||
    values[Fields.ServingNumber].trim().length < 1
  ) {
    errors[Fields.ServingNumber] = 'Serving number is required.';
  } else if (values[Fields.ServingNumber].trim().length < 3) {
    errors[Fields.ServingNumber] =
      'Serving number must be between 3 and 12 characters.';
  }

  if (!values[Fields.Id] || values[Fields.Id].trim().length < 1) {
    errors[Fields.Id] = 'Student number is required.';
  } else if (values[Fields.Id].trim().length < 3) {
    errors[Fields.Id] = 'Student number must be between 3 and 12 characters.';
  }

  if (!values[Fields.SiteId] || values[Fields.SiteId].trim().length < 1) {
    errors[Fields.SiteId] = 'Site is required.';
  }

  if (
    values[Fields.SSN] &&
    values[Fields.SSN] !== undefined &&
    values[Fields.SSN].length > 0 &&
    values[Fields.SSN].length < 4
  ) {
    errors[Fields.SSN] = 'Invalid SSN.';
  }

  errors.Income = validateIncome(values.Income);
  errors.Exceptions = validateExceptions(values.Exceptions);

  return errors;
};

const asyncValidate = (values, dispatch, props) => {
  const studentIds = {
    StudentNumber: values[Fields.Id],
    ServingNumber: values[Fields.ServingNumber],
  };

  if (!props.isNewStudent) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    MPWApi.postGenericInsertUpdate(
      '/Student/GetCheckStudentIds',
      studentIds
    ).then(values => {
      const errors = {};

      if (!values.data.Data.StudentNumberIsValid) {
        errors[Fields.Id] = 'Student number invalid.';
      }
      if (!values.data.Data.StudentNumberIsAvailable) {
        errors[Fields.Id] = 'Student number not available.';
      }

      if (!values.data.Data.ServingNumberIsValid) {
        errors[Fields.ServingNumber] = 'Serving number invalid.';
      }
      if (!values.data.Data.ServingNumberIsAvailable) {
        errors[Fields.ServingNumber] = 'Serving number not available.';
      }

      if (Object.keys(errors).length > 0) {
        reject(errors);
      } else {
        resolve();
      }
    });
  });
};

export default reduxForm({
  validate,
  asyncValidate,
  asyncBlurFields: ['Id', 'ServingNumber'],
  form: APPLICATION_STUDENT_FORM_NAME,
  destroyOnUnmount: true,
  enableReinitialize: true,
  touchOnChange: true,
  touchOnBlur: true,
})(StudentForm);



// WEBPACK FOOTER //
// ./src/pages/manageapplication/components/student/StudentForm.js